import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { Container, Body, Content as DefaultContent } from '../DefaultStyles/Details';
import {
    Footer,
    Title,
    DetailsContainer as DefaultDetailsContainer,
    Details
} from '../DefaultStyles/Shared';
import OverlayPartner from '../../containers/Widget/OverlayPartner';

import CssTransition from '../../components/Transition/CssTransition';
import TransitionContainer from '../../components/Transition/TranstionContainer';
import ToasterAlert from '../../components/Donation/ToasterAlert';
import PetitionForm from '../../components/Petition/PetitionForm';
import RoundImage from '../../components/Image/RoundImage';
import ShareDrawer from '../../components/ShareDrawer';
import { isLongText } from '../../components/Shared/commonFunctions';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayPetition from './PetitionOverlay';

import {
    onBackWithinCard,
    onGoToNextCard,
    setOverlayPartnerVisible
} from '../Widget/actions';

import { onPetitionComplete, setOverlayPetitionVisible, onInitiated } from './actions';

import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { cacheFormData } from '../../utils/formCaching/actions';
import { isPortrait } from '../../utils/size';
import { Placeholders } from '../../utils/fillDynamicText';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { CardTypes } from '../../utils/enums';
import { pictureUrl } from '../../utils/imageLoading';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';

import { copyFor } from '../../copy/copy';

const Content = styled(DefaultContent)`
    margin-top: 1em;
    display: ${() => (isPortrait() ? 'block' : 'flex')};
`;

const DetailsContainer = styled(DefaultDetailsContainer)`
    flex-direction: row;
    align-items: flex-start;
`;

const Description = styled.p`
    object-fit: contain;
    font-family: Lato;
    line-height: 1.5;
    color: #333;
    margin-bottom: 0;
    word-break: break-word;
    font-size: 0.85em;
    font-weight: 800;

    #learnMore {
        font-weight: 800;
        color: #ff245e;
        cursor: pointer;
    }
`;

const horizontalBlocks = `
    width: 50%;
    height: 100%;
    margin: 0 0 0 15px;
    &:first-child {
        margin: 0 15px 0 0;
    }
`;
const verticalBlocks = `
    width: 100%;
    &:last-child {
        height: calc(50% + 20px);
    }
`;

const Block = styled.div`
    ${() => (isPortrait() ? verticalBlocks : horizontalBlocks)}
`;

const LearnMore = styled.span`
    font-weight: 800;
    font-size: 0.85em;
    line-height: 2;
    color: #ff245e;
    cursor: pointer;
`;

const PetitionDetails = props => (
    <TransitionContainer zIndex={2} isSubmitting={props.isSubmitting}>
        <Container>
            <Overlay />
            <OverlayPublisher />
            <OverlayPartner transitionType="right-to-left" />
            <OverlayPetition transitionType="right-to-left" />
            <Body>
                <WidgetHeader />
                <Content>
                    <Block>
                        <Title
                            isShortText={!isLongText(props.cardTitle, 28)}
                            isLongText={isLongText(props.cardTitle, 60)}
                            isExtraLongText={isLongText(props.cardTitle, 95)}
                            onClick={e => props.setOverlayPartnerVisible(true, e, false)}
                            dangerouslySetInnerHTML={{
                                __html: props.cardTitle
                            }}
                        />
                        <DetailsContainer>
                            {!!props.pictureUrl && (
                                <RoundImage src={props.pictureUrl} size={`60px`} />
                            )}
                            <Details hasPhoto={!!props.pictureUrl}>
                                <Description
                                    dangerouslySetInnerHTML={{
                                        __html: props.cardDescription
                                    }}
                                    onClick={e => {
                                        props.onInitiated(
                                            props.actionInitiated,
                                            props.actionIdentifier
                                        );
                                        if (e.target.id === 'learnMore') {
                                            props.setOverlayPetitionVisible(true);
                                        }
                                    }}
                                    isSingleAction={true}
                                    isLongText={isLongText(props.cardDescription, 90)}
                                    isExtraLongText={isLongText(
                                        props.cardDescription,
                                        160
                                    )}
                                />
                                {(!props.isDisplayMoreInline || !isPortrait()) && (
                                    <LearnMore
                                        onClick={() => {
                                            props.onInitiated(
                                                props.actionInitiated,
                                                props.actionIdentifier
                                            );
                                            props.setOverlayPetitionVisible(true);
                                        }}
                                    >
                                        {copyFor(
                                            'miscellaneous',
                                            'learnMore',
                                            'petition'
                                        )}
                                    </LearnMore>
                                )}
                            </Details>
                        </DetailsContainer>
                    </Block>
                    <Block>
                        <PetitionForm
                            onSubmit={values => {
                                props.cacheFormData(values);
                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }
                                props.onPetitionComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    values,
                                    props.isMultiActionsCard,
                                    props.activeAction,
                                    props.skipEndCard
                                );
                            }}
                            response={props.response}
                            isSubmitting={props.isSubmitting}
                            onInitiated={() =>
                                props.onInitiated(
                                    props.actionInitiated,
                                    props.actionIdentifier
                                )
                            }
                        />
                    </Block>
                </Content>
                <Footer>
                    <ShareDrawer
                        newsletterSignUpVisible={true}
                        goBack={() =>
                            props.card.type === CardTypes.actionChoice
                                ? props.onBackWithinCard(
                                      props.actionIdentifier,
                                      props.card,
                                      true
                                  )
                                : null
                        }
                        fullwidth={true}
                        hasBackButton={props.card.type === CardTypes.actionChoice}
                        backgroundColor={'#fff'}
                    />
                </Footer>
            </Body>
            <TransitionGroup>
                {props.petitionError && (
                    <CssTransition key="alert" in={props.petitionError}>
                        <ToasterAlert content={props.petitionError} />
                    </CssTransition>
                )}
            </TransitionGroup>
        </Container>
    </TransitionContainer>
);

PetitionDetails.propTypes = {
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool,
    activeAction: PropTypes.object.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    card: PropTypes.object.isRequired,
    cardDescription: PropTypes.string.isRequired,
    cards: PropTypes.array.isRequired,
    cardTitle: PropTypes.string.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    isDisplayMoreInline: PropTypes.bool.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    nextAction: PropTypes.object,
    onBackWithinCard: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    onInitiated: PropTypes.func.isRequired,
    onPetitionComplete: PropTypes.func.isRequired,
    petitionError: PropTypes.string,
    pictureUrl: PropTypes.string,
    response: PropTypes.string,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    setOverlayPetitionVisible: PropTypes.func.isRequired,
    skipEndCard: PropTypes.bool
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );
        const currCard = state.widget.cards[state.widget.activeAction.order - 1];
        const unformattedCardDescription =
            currCard.subHeading || state.widget.activeAction.description || '';
        const learnMoreString = ` <span id="learnMore">${copyFor(
            'miscellaneous',
            'learnMore',
            'petition'
        )}</span>`;

        return {
            nextAction,
            actionCompletedTime: getActionCompletedTime(state, 'petition'),
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.petition.actionInitiated,
            activeAction: state.widget.activeAction,
            apiRequestData: state.widget.apiRequestData,
            card: currCard,
            cardDescription: unformattedCardDescription
                .concat(
                    unformattedCardDescription.length > 50 && isPortrait()
                        ? learnMoreString
                        : ''
                )
                .replace(
                    new RegExp(Placeholders.ArticleUrl, 'g'),
                    state.widget.articleUrl
                )
                .replace(
                    new RegExp(Placeholders.NumberOfAnswers, 'g'),
                    state.widget.activeAction.noOfAnswers || '121'
                ),
            cardTitle: (currCard.heading || state.widget.activeAction.name || '').replace(
                /@articleUrl/g,
                state.widget.articleUrl
            ),
            isDisplayMoreInline: unformattedCardDescription.length > 50,
            isSubmitting: state.petition.isSubmitting,
            response: state.widget.activeAction.text,
            cards: state.widget.cards,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            petitionError: state.petition.petitionError,
            pictureUrl: pictureUrl(state),
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction
        };
    },
    {
        onBackWithinCard,
        onPetitionComplete,
        onGoToNextCard,
        onInitiated,
        handleSkippingEndCard,
        cacheFormData,
        setOverlayPartnerVisible,
        setOverlayPetitionVisible
    }
)(PetitionDetails);
